import React, { useEffect, useState } from 'react';
import { userSkillsAPI } from '../services/api';
import AddSkillModal from '../components/modals/AddSkillModal.tsx';
import toast from 'react-hot-toast';

// ── Built-in Lyra Skills ─────────────────────────────────────────────────────
const LYRA_SKILLS = [
  {
    id: 'lyra:gmail-reader',
    name: 'Gmail Reader',
    description: 'List and summarize unread Gmail messages from your connected account.',
    icon: '✉️',
    domain: 'communication',
    tags: ['gmail', 'email', 'reader'],
    endpoints: [
      { label: 'List messages', example: 'GET /api/gmail/messages?q=is:unread&maxResults=10' },
      { label: 'Get message',   example: 'GET /api/gmail/messages/:id?format=full' },
    ],
    setupNote: 'Requires Gmail account connected via Settings → Connections.',
  },
  {
    id: 'lyra:calendar-assistant',
    name: 'Calendar Assistant',
    description: 'Read upcoming events and create bookings in your connected Google Calendar.',
    icon: '📆',
    domain: 'productivity',
    tags: ['calendar', 'google'],
    endpoints: [
      { label: 'List events', example: 'GET /api/calendar/events?timeMin=2026-06-04T00:00:00Z&timeMax=2026-06-11T00:00:00Z' },
      { label: 'Create event', example: 'POST /api/calendar/events { summary, start: {dateTime}, end: {dateTime} }' },
    ],
    setupNote: 'Requires Google Calendar connected via Settings → Connections.',
  },
  {
    id: 'lyra:teams-connector',
    name: 'Microsoft Teams Connector',
    description: 'Connect Microsoft Teams to read channel messages and post notifications.',
    icon: '🤝',
    domain: 'communication',
    tags: ['microsoft','teams'],
    endpoints: [
      { label: 'List messages', example: 'GET /api/teams/messages?channelId=xxx&max=25' },
      { label: 'Post to channel', example: 'POST /api/teams/post { channelId, body }' },
    ],
    setupNote: 'Requires Microsoft account connected via Settings → Connections.',
  },
];

// ── Tiny card sub-components ─────────────────────────────────────────────────
function LyraSkillCard({ skill }: { skill: typeof LYRA_SKILLS[0] }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      border: '1.5px solid var(--lyra-color-primary)',
      borderRadius: 10,
      padding: 14,
      background: 'linear-gradient(135deg,var(--app-primary-soft) 0%,var(--app-primary-soft) 100%)',
      position: 'relative',
    }}>
      {/* Badge */}
      <span style={{
        position: 'absolute', top: 10, right: 12,
        fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
        background: 'var(--lyra-color-primary)', color: 'var(--app-on-accent)',
        borderRadius: 99, padding: '2px 8px',
      }}>LYRA</span>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 22 }}>{skill.icon}</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{skill.name}</div>
          <div style={{ color: '#555', fontSize: 12 }}>{skill.domain}</div>
        </div>
      </div>

      <div style={{ color: 'var(--app-text-2)', fontSize: 13, marginBottom: 10 }}>{skill.description}</div>

      {/* Tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
        {skill.tags.map(t => (
          <span key={t} style={{ fontSize: 11, background: 'var(--app-primary-soft)', color: 'var(--lyra-color-primary)', borderRadius: 99, padding: '2px 8px', border: '1px solid #c4b5fd' }}>{t}</span>
        ))}
      </div>

      {/* Expand endpoints */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{ fontSize: 12, color: 'var(--lyra-color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
      >
        {open ? 'Hide endpoints ▲' : 'View endpoints ▼'}
      </button>

      {open && (
        <div style={{ marginTop: 8 }}>
          {skill.endpoints.map(e => (
            <div key={e.label} style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--lyra-color-primary)' }}>{e.label}</div>
              <code style={{ display: 'block', fontSize: 11, background: 'var(--app-surface-3)', borderRadius: 4, padding: '3px 6px', wordBreak: 'break-all' }}>{e.example}</code>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--lyra-color-text-muted)', marginTop: 4 }}>⚠ {skill.setupNote}</div>
        </div>
      )}
    </div>
  );
}

export default function SkillsManagement() {
  const [skills, setSkills] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any | null>(null);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const mySkills = await userSkillsAPI.list();
      setSkills(Array.isArray(mySkills) ? mySkills : mySkills || []);
    } catch (err) {
      console.error('Failed to load user skills', err);
      toast.error('Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleEdit = (s: any) => {
    setEditing(s);
    setShowModal(true);
  };

  const handleCreatedOrUpdated = (res: any) => {
    const id = res._id || res.id;
    setSkills(prev => {
      const found = prev.findIndex(p => (p._id || p.id) === id);
      if (found >= 0) {
        const copy = [...prev]; copy[found] = res; return copy;
      }
      return [res, ...prev];
    });
    toast.success('Saved');
  };

  const handleDeleted = (id: string) => {
    setSkills(prev => prev.filter(s => (s._id || s.id) !== id));
    toast.success('Deleted');
  };

  const handleRemove = async (s: any) => {
    if (!window.confirm('Delete this skill?')) return;
    try {
      await userSkillsAPI.remove(s._id || s.id);
      handleDeleted(s._id || s.id);
    } catch (err) {
      console.error('Delete failed', err);
      toast.error('Delete failed');
    }
  };

  return (
    <div style={{ padding: 24 }}>

      {/* ── Lyra Skills section ── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Lyra Skills</h2>
          <span style={{ fontSize: 12, color: 'var(--lyra-color-primary)', background: 'var(--app-primary-soft)', borderRadius: 99, padding: '2px 10px', fontWeight: 600 }}>Built-in</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(290px,1fr))', gap: 12 }}>
          {LYRA_SKILLS.map(s => <LyraSkillCard key={s.id} skill={s} />)}
        </div>
      </div>

      {/* ── My Skills section ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h2 style={{ margin: 0 }}>My Skills</h2>
        <div>
          <button onClick={() => { setEditing(null); setShowModal(true); }} className="btn btn-primary">Add Skill</button>
          <button onClick={load} style={{ marginLeft: 8 }} className="btn">Refresh</button>
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : skills.length === 0 ? (
        <div style={{ color: 'var(--lyra-color-text-muted)', fontSize: 14 }}>No custom skills yet. Click <strong>Add Skill</strong> to create one.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
          {skills.map(s => (
            <div key={s._id || s.id} style={{ border: '1px solid var(--app-surface-3)', padding: 12, borderRadius: 8, background: 'var(--lyra-color-surface)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{s.name}</div>
                  <div style={{ color: 'var(--app-text-2)', fontSize: 13 }}>{s.description}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--lyra-color-text-muted)' }}>{s.domain || 'user'}</div>
                </div>
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button onClick={() => handleEdit(s)} className="btn">Edit</button>
                <button onClick={() => handleRemove(s)} className="btn btn-danger">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddSkillModal
        isOpen={showModal}
        initial={editing || undefined}
        onClose={() => setShowModal(false)}
        onCreated={handleCreatedOrUpdated}
        onDeleted={handleDeleted}
      />
    </div>
  );
}
